/**
 * design.md §4 — one cell per semantic type.
 *
 * This and FieldRenderer are the generic table's and form's only atoms. Adding a
 * semantic type means adding a case to both; there is no per-app cell code
 * anywhere in the project.
 */

import { MapPin } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Empty, UserCell } from '@/components/bits'
import { StatusPill } from '@/components/ui/surface'
import { AttachmentCell } from '@/render/Attachments'
import type { BadgeProps } from '@/components/ui/surface'
import type { DataRecord, FieldDef, RecordPage } from '@/lib/meta'
import { refLabel, valueField } from '@/lib/meta'
import { formatDate, formatDateTime, relativeDay, relativeTime } from '@/lib/utils'
import { LinkedPhonesById, telHref } from '@/render/LinkedPhones'

/** Option tones come from metadata, so a select's colour is data, not code. */
const TONES: Record<string, BadgeProps['tone']> = {
  neutral: 'neutral',
  info: 'info',
  success: 'success',
  warn: 'warn',
  danger: 'danger',
  accent: 'accent',
}

/** "26.91243,75.78727" → "26.9124, 75.7873". */
export function formatLocation(value: string): string {
  const [lat, lng] = value.split(',').map((part) => Number(part.trim()))
  if (Number.isFinite(lat) && Number.isFinite(lng)) return `${lat.toFixed(4)}, ${lng.toFixed(4)}`
  return value
}

export function isNumeric(field: FieldDef): boolean {
  return ['integer', 'decimal', 'percent', 'currency'].includes(valueField(field).type)
}

const currency = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
})
const decimal = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 })

export function CellRenderer({
  field,
  record,
  refs,
  full = false,
}: {
  field: FieldDef
  record: DataRecord
  refs: RecordPage['refs']
  /** On a record's page: long text whole and wrapped, not its first line (§4). */
  full?: boolean
}): ReactNode {
  // A computed field renders exactly as the type it computes (§4 "computed").
  if (field.type === 'computed') {
    return <CellRenderer field={valueField(field)} record={record} refs={refs} full={full} />
  }

  const value = record[field.slug]

  if (value === null || value === undefined || value === '') {
    // design.md §5: empty is an em dash, never a blank cell.
    return <Empty />
  }

  switch (field.type) {
    case 'select': {
      const option = field.options?.find((o) => o.value === value)
      return (
        <StatusPill tone={TONES[option?.tone ?? 'neutral'] ?? 'neutral'}>
          {option?.label ?? String(value)}
        </StatusPill>
      )
    }

    case 'multi_select': {
      const items = Array.isArray(value) ? value : []
      if (items.length === 0) return <Empty />
      return (
        <span className="flex flex-wrap items-center gap-1">
          {items.slice(0, 2).map((item) => {
            const option = field.options?.find((o) => o.value === item)
            return (
              <StatusPill key={String(item)} tone={TONES[option?.tone ?? 'neutral'] ?? 'neutral'}>
                {option?.label ?? String(item)}
              </StatusPill>
            )
          })}
          {items.length > 2 ? (
            <span className="text-xs text-faint">+{items.length - 2}</span>
          ) : null}
        </span>
      )
    }

    case 'boolean':
      // Never an empty cell: false is information.
      return <span className="text-muted">{value ? '✓' : '—'}</span>

    case 'currency':
      return <span className="tabular">{currency.format(Number(value))}</span>

    case 'percent':
      return <span className="tabular">{decimal.format(Number(value))}%</span>

    case 'integer':
    case 'decimal':
      return <span className="tabular">{decimal.format(Number(value))}</span>

    case 'date':
      // A date read by how long ago it was (`relative`: the last activity) says so; the
      // date itself is on hover.
      if (field.config?.relative === true) {
        return (
          <span className="whitespace-nowrap" title={formatDate(String(value)) ?? undefined}>
            {relativeDay(String(value)) ?? formatDate(String(value))}
          </span>
        )
      }
      return <span className="tabular whitespace-nowrap">{formatDate(String(value))}</span>

    case 'sequence':
      return <span className="tabular whitespace-nowrap">{String(value)}</span>

    case 'datetime':
      // design.md §4: the date and time in the organisation's zone, how long ago on hover.
      return (
        <time
          dateTime={String(value)}
          title={relativeTime(String(value)) ?? undefined}
          className="tabular whitespace-nowrap"
        >
          {formatDateTime(String(value))}
        </time>
      )

    case 'email':
      return (
        <a
          href={`mailto:${value}`}
          title={String(value)}
          className="block truncate text-accent hover:underline"
        >
          {String(value)}
        </a>
      )

    case 'phone':
      return (
        <a href={telHref(String(value))} className="text-accent hover:underline">
          {String(value)}
        </a>
      )

    case 'location':
      // "lat,lng", shown rounded and opening the map.
      return (
        <a
          href={`https://www.google.com/maps?q=${encodeURIComponent(String(value))}`}
          target="_blank"
          rel="noreferrer noopener"
          className="tabular inline-flex items-center gap-1 text-accent hover:underline"
          title="Open in Google Maps"
        >
          <MapPin className="size-3" aria-hidden />
          {formatLocation(String(value))}
        </a>
      )

    case 'url':
      return (
        <a
          href={String(value)}
          target="_blank"
          rel="noreferrer noopener"
          title={String(value)}
          className="block truncate text-accent hover:underline"
        >
          {String(value).replace(/^https?:\/\//, '')}
        </a>
      )

    case 'reference': {
      // design.md §4: a reference is a link to the record it names, everywhere.
      const label = refLabel(refs, field, value)
      const [app, table] = (field.references ?? '').split('.')
      if (!label) {
        return <span className="font-mono text-xs text-faint">{String(value).slice(0, 8)}</span>
      }
      const link = app && table ? (
        <Link
          to={`/app/${app}/${table}/${String(value)}`}
          // Inside a clickable row, follow this link, not the row's.
          onClick={(event) => event.stopPropagation()}
          className="text-text underline decoration-border underline-offset-2 hover:text-accent hover:decoration-accent"
        >
          {label}
        </Link>
      ) : null
      // On a record's page a contact shows its phone too, one tap from a call.
      if (link && full && field.references) {
        return (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {link}
            <LinkedPhonesById reference={field.references} id={String(value)} />
          </span>
        )
      }
      return link ?? (
        <span className="text-text">{label}</span>
      )
    }

    case 'user': {
      const label = refs['sys_user']?.[String(value)]
      return label ? <UserCell name={label} email="" /> : <Empty />
    }

    case 'users': {
      // design.md §4: several people — names, the first two in a cell, all on a page.
      const names = (Array.isArray(value) ? value : [])
        .map((id) => refs['sys_user']?.[String(id)])
        .filter((name): name is string => Boolean(name))
      if (names.length === 0) return <Empty />
      const shown = full ? names : names.slice(0, 2)
      return (
        <span className="block truncate" title={names.join(', ')}>
          {shown.join(', ')}
          {names.length > shown.length ? (
            <span className="text-faint"> +{names.length - shown.length}</span>
          ) : null}
        </span>
      )
    }

    case 'attachment':
      return <AttachmentCell value={value} full={full} />

    case 'record': {
      // "app.table:<id>" — a record in any table, linked by its name (design.md §4).
      const [key, id] = String(value).split(':')
      const [app, table] = key.split('.')
      const label = refs[key]?.[id]
      return (
        <Link
          to={`/app/${app}/${table}/${id}`}
          onClick={(event) => event.stopPropagation()}
          className="text-text underline decoration-border underline-offset-2 hover:text-accent hover:decoration-accent"
        >
          {label ?? `${table} ${id.slice(0, 8)}`}
        </Link>
      )
    }

    case 'json':
      return <span className="font-mono text-xs text-muted">{'{…}'}</span>

    case 'long_text': {
      const text = String(value)
      if (full) return <span className="block break-words whitespace-pre-wrap">{text}</span>
      return (
        <span className="block max-w-xs truncate" title={text}>
          {text.split('\n')[0]}
        </span>
      )
    }

    default:
      // design.md §3 Long values: one line, trimmed with … and whole on hover — on a
      // record's page as wide as the field has room for, in a list cell at most max-w-xs.
      if (full) {
        return (
          <span className="block truncate" title={String(value)}>
            {String(value)}
          </span>
        )
      }
      return (
        <span className="block max-w-xs truncate" title={String(value)}>
          {String(value)}
        </span>
      )
  }
}
